<template>
  <div
    :class="rootClass"
    @focusin="uiStore.touchSession(props.sessionId)"
  >
    <div :class="panelClass">
      <!-- Barra de título: solo en flotante. El cierre en línea vive en la
           barra de comandos; minimizado se puede restaurar haciendo clic. -->
      <div
        v-if="!inline"
        class="h-10 px-3 flex items-center gap-1 bg-brand-navy text-white select-none flex-shrink-0"
        :class="windowState === 'minimized' ? 'cursor-pointer' : ''"
        @click="windowState === 'minimized' && uiStore.focusSession(props.sessionId)"
      >
        <span class="flex-1 min-w-0 truncate text-[13px] font-medium">{{ windowTitle }}</span>
        <button
          type="button"
          class="w-7 h-7 rounded flex items-center justify-center hover:bg-white/10 flex-shrink-0"
          :title="t('emailComposer.window.minimize')"
          :aria-label="t('emailComposer.window.minimize')"
          @click.stop="toggleMinimize"
        >
          <Minus class="w-4 h-4" />
        </button>
        <button
          type="button"
          class="w-7 h-7 rounded flex items-center justify-center hover:bg-white/10 flex-shrink-0"
          :title="windowState === 'maximized' ? t('emailComposer.window.exitMaximize') : t('emailComposer.window.maximize')"
          :aria-label="windowState === 'maximized' ? t('emailComposer.window.exitMaximize') : t('emailComposer.window.maximize')"
          @click.stop="toggleMaximize"
        >
          <Minimize2 v-if="windowState === 'maximized'" class="w-4 h-4" />
          <Maximize2 v-else class="w-4 h-4" />
        </button>
        <button
          type="button"
          class="w-7 h-7 rounded flex items-center justify-center hover:bg-white/10 flex-shrink-0"
          :title="t('emailComposer.close')"
          :aria-label="t('emailComposer.close')"
          @click.stop="closeComposer"
        >
          <X class="w-4 h-4" />
        </button>
      </div>

      <div v-show="inline || windowState !== 'minimized'" class="flex-1 min-h-0 flex flex-col">
      <!-- Barra de comandos del redactor -->
      <div class="h-[52px] px-4 flex items-center gap-2 border-b border-secondary-200 dark:border-secondary-700 flex-shrink-0 overflow-x-auto">
        <button
          @click="sendEmail"
          :disabled="!canSend || isSending"
          class="h-9 px-4 rounded-lg bg-kila-mint text-brand-navy text-[13px] font-bold flex items-center gap-2 hover:brightness-95 transition disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
        >
          <Send class="w-4 h-4" />
          <span>{{ isSending ? t('emailComposer.sending') : t('emailComposer.send') }}</span>
        </button>
        <button
          @click="showDiscardModal = true"
          class="h-9 px-2.5 rounded-lg flex items-center gap-1.5 text-[13px] font-medium text-secondary-600 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors flex-shrink-0"
        >
          <Trash2 class="w-[18px] h-[18px]" />
          <span>{{ t('emailWorkspace.composer.discard') }}</span>
        </button>

        <div class="w-px h-6 bg-secondary-200 dark:bg-secondary-700 mx-1 flex-shrink-0"></div>

        <!-- Adjuntar -->
        <div class="relative flex-shrink-0" ref="attachmentDropdownRef">
          <button
            @click="showAttachmentDropdown = !showAttachmentDropdown"
            class="h-9 px-2.5 rounded-lg flex items-center gap-1.5 text-[13px] font-medium text-secondary-600 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
            :title="t('emailComposer.toolbar.attachFile')"
          >
            <Paperclip class="w-[18px] h-[18px]" />
            <span>{{ t('emailWorkspace.composer.attach') }}</span>
            <ChevronDown class="w-3.5 h-3.5 opacity-70" />
          </button>
          <Teleport to="body">
            <div
              v-if="showAttachmentDropdown"
              ref="attachmentMenuRef"
              :style="attachmentMenuStyle"
              class="fixed w-60 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-xl shadow-xl z-[60] overflow-hidden"
            >
              <label
                class="flex items-center gap-3 px-4 py-2.5 hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors cursor-pointer"
                @click="showAttachmentDropdown = false"
              >
                <HardDrive class="w-4 h-4 text-secondary-500" />
                <span class="text-sm text-secondary-900 dark:text-white">{{ t('emailComposer.toolbar.fromDevice') }}</span>
                <input type="file" multiple @change="handleFileSelect" class="hidden" />
              </label>
              <button
                v-if="props.attachmentSource"
                @click="openDocumentPicker"
                class="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors text-left"
              >
                <Ship v-if="props.attachmentSource.type === 'shipment'" class="w-4 h-4 text-primary-500" />
                <ShoppingCart v-else class="w-4 h-4 text-primary-500" />
                <span class="text-sm text-secondary-900 dark:text-white">
                  {{ props.attachmentSource.type === 'shipment' ? t('emailComposer.toolbar.fromShipment') : t('emailComposer.toolbar.fromPO') }}
                </span>
              </button>
            </div>
          </Teleport>
        </div>

        <!-- Plantillas -->
        <div class="relative flex-shrink-0" ref="templateDropdownRef">
          <button
            @click="showTemplateDropdown = !showTemplateDropdown"
            class="h-9 px-2.5 rounded-lg flex items-center gap-1.5 text-[13px] font-medium border transition-colors"
            :class="showTemplateDropdown || appliedTemplateName
              ? 'border-kila-mint bg-kila-mint-subtle text-primary-700 dark:text-brand-mint'
              : 'border-transparent text-secondary-600 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700'"
            :title="t('emailComposer.templates.button')"
          >
            <LayoutTemplate class="w-[18px] h-[18px]" />
            <span class="max-w-[180px] truncate">{{ appliedTemplateName || t('emailComposer.templates.button') }}</span>
            <ChevronDown class="w-3.5 h-3.5 opacity-70" />
          </button>
          <Teleport to="body">
            <div
              v-if="showTemplateDropdown"
              ref="templateMenuRef"
              :style="templateMenuStyle"
              class="fixed w-72 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-xl shadow-xl z-[60] overflow-hidden"
            >
              <div class="flex items-center justify-between p-3 border-b border-secondary-100 dark:border-secondary-700">
                <p class="text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase">{{ t('emailComposer.templates.available') }}</p>
                <button
                  v-if="canManageTemplates"
                  @click.stop="openTemplateEditor"
                  class="flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                  :title="t('emailComposer.templates.new')"
                >
                  <Plus class="w-3.5 h-3.5" />
                  <span>{{ t('emailComposer.templates.new') }}</span>
                </button>
              </div>
              <div class="max-h-48 overflow-y-auto">
                <div v-if="templateStore.visibleTemplates.length === 0" class="p-4 text-center text-sm text-secondary-400">
                  {{ t('emailComposer.templates.noTemplates') }}
                </div>
                <button
                  v-for="tpl in templateStore.visibleTemplates"
                  :key="tpl.id"
                  @click="applyTemplate(tpl)"
                  class="w-full text-left px-4 py-2.5 hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors border-b border-secondary-50 dark:border-secondary-700/50 last:border-0"
                >
                  <div class="flex items-center justify-between gap-2">
                    <p class="text-sm font-medium text-secondary-900 dark:text-white truncate">{{ tpl.name }}</p>
                    <span
                      v-if="tpl.visibility === 'private'"
                      class="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 dark:text-amber-400"
                      :title="t('emailComposer.templates.onlyYou')"
                    >
                      <Lock class="w-3 h-3" />
                    </span>
                  </div>
                  <p class="text-xs text-secondary-400 truncate">{{ tpl.description || tpl.subject }}</p>
                </button>
              </div>
            </div>
          </Teleport>
        </div>

        <!-- Firmas -->
        <div class="relative flex-shrink-0" ref="signatureDropdownRef">
          <button
            @click="showSignatureDropdown = !showSignatureDropdown"
            class="h-9 px-2.5 rounded-lg flex items-center gap-1.5 text-[13px] font-medium text-secondary-600 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
            :class="{ 'bg-secondary-100 dark:bg-secondary-700': showSignatureDropdown }"
            :title="t('emailComposer.signatures.button')"
          >
            <PenLine class="w-[18px] h-[18px]" />
            <span>{{ t('emailWorkspace.composer.signature') }}</span>
            <ChevronDown class="w-3.5 h-3.5 opacity-70" />
          </button>
          <Teleport to="body">
            <div
              v-if="showSignatureDropdown"
              ref="signatureMenuRef"
              :style="signatureMenuStyle"
              class="fixed w-72 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-xl shadow-xl z-[60] overflow-hidden"
            >
              <div class="flex items-center justify-between p-3 border-b border-secondary-100 dark:border-secondary-700">
                <p class="text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase">{{ t('emailComposer.signatures.available') }}</p>
                <button
                  @click.stop="showSignatureDropdown = false; showSignatureManager = true"
                  class="flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                  :title="t('emailComposer.signatures.manage')"
                >
                  <Plus class="w-3.5 h-3.5" />
                  <span>{{ t('emailComposer.signatures.manage') }}</span>
                </button>
              </div>
              <div class="max-h-48 overflow-y-auto">
                <div v-if="signatureStore.userSignatures.length === 0" class="p-4 text-center text-sm text-secondary-400">
                  {{ t('emailComposer.signatures.noSignatures') }}
                </div>
                <button
                  v-for="sig in signatureStore.userSignatures"
                  :key="sig.id"
                  @click="insertSignature(sig)"
                  class="w-full text-left px-4 py-2.5 hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors border-b border-secondary-50 dark:border-secondary-700/50 last:border-0"
                >
                  <div class="flex items-center justify-between gap-2">
                    <p class="text-sm font-medium text-secondary-900 dark:text-white truncate">{{ sig.name }}</p>
                    <Star
                      v-if="sig.isDefault"
                      class="w-3.5 h-3.5 flex-shrink-0 text-vicval-yellow fill-vicval-yellow"
                      :title="t('emailComposer.signatures.default')"
                    />
                  </div>
                </button>
              </div>
              <div v-if="hasActiveSignature" class="border-t border-secondary-100 dark:border-secondary-700">
                <button
                  @click="removeExistingSignature(); showSignatureDropdown = false"
                  class="w-full text-left px-4 py-2 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                >
                  {{ t('emailComposer.signatures.removeFromEmail') }}
                </button>
              </div>
            </div>
          </Teleport>
        </div>

        <!-- Prioridad -->
        <div class="relative flex-shrink-0" ref="priorityDropdownRef">
          <button
            type="button"
            class="h-9 px-2.5 rounded-lg flex items-center gap-1.5 text-[13px] font-medium text-secondary-600 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
            :class="{ 'bg-secondary-100 dark:bg-secondary-700': showPriorityDropdown }"
            :title="t('emailWorkspace.composer.priority')"
            @click="showPriorityDropdown = !showPriorityDropdown"
          >
            <AlertTriangle class="w-[18px] h-[18px]" :class="emailData.importance === 'high' ? 'text-red-500' : ''" />
            <span>{{ t(`emailComposer.priority.${emailData.importance}`) }}</span>
            <ChevronDown class="w-3.5 h-3.5 opacity-70" />
          </button>
          <Teleport to="body">
            <div
              v-if="showPriorityDropdown"
              ref="priorityMenuRef"
              :style="priorityMenuStyle"
              class="fixed w-56 py-1 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-xl shadow-xl z-[60] overflow-hidden"
            >
              <button
                v-for="level in PRIORITY_LEVELS"
                :key="level"
                type="button"
                class="w-full px-3 py-2 flex items-center gap-2.5 text-[13px] text-left transition-colors hover:bg-secondary-50 dark:hover:bg-secondary-700"
                :class="emailData.importance === level ? 'text-brand-navy dark:text-white font-semibold' : 'text-secondary-700 dark:text-secondary-200'"
                @click="emailData.importance = level; showPriorityDropdown = false"
              >
                <Check class="w-4 h-4 flex-shrink-0" :class="emailData.importance === level ? 'text-primary-700 dark:text-brand-mint' : 'invisible'" />
                <span class="flex-1">{{ t(`emailComposer.priority.${level}`) }}</span>
                <AlertTriangle v-if="level === 'high'" class="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
              </button>
            </div>
          </Teleport>
        </div>

        <div class="flex-1"></div>

        <span class="text-xs text-secondary-500 dark:text-secondary-400 whitespace-nowrap hidden lg:inline">{{ composerTitle }}</span>
        <button
          v-if="inline"
          @click="uiStore.popOutSession(props.sessionId)"
          class="h-9 px-2.5 rounded-lg flex items-center gap-1.5 text-[13px] font-medium text-secondary-600 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors whitespace-nowrap flex-shrink-0"
          :title="t('emailComposer.window.popOut')"
        >
          <ExternalLink class="w-4 h-4" />
          <span>{{ t('emailComposer.window.popOut') }}</span>
        </button>
        <button
          @click="saveDraft"
          :disabled="isSaving"
          class="h-9 px-2.5 rounded-lg text-[13px] font-medium text-secondary-600 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors disabled:opacity-50 whitespace-nowrap flex-shrink-0"
        >
          {{ isSaving ? t('emailComposer.saving') : t('emailComposer.saveDraft') }}
        </button>
        <!-- En flotante el cierre vive en la barra de título; aquí solo en línea. -->
        <button
          v-if="inline"
          @click="closeComposer"
          class="w-9 h-9 rounded-lg flex items-center justify-center text-secondary-600 dark:text-secondary-400 hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors flex-shrink-0"
          :title="t('emailComposer.close')"
        >
          <X class="w-[18px] h-[18px]" />
        </button>
      </div>

      <!-- Formulario -->
      <div class="flex-1 min-h-0 overflow-y-auto px-6 flex flex-col">
        <!-- Para -->
        <div class="min-h-10 py-1 flex items-center gap-3 border-b border-secondary-200 dark:border-secondary-700 flex-shrink-0">
          <span :class="fieldLabelClass">{{ t('emailComposer.to') }}</span>
          <RecipientInput
            v-model="emailData.to"
            v-model:input-value="emailData.toInput"
            :placeholder="t('emailComposer.recipientPlaceholder')"
            pill-color-class="bg-secondary-100 dark:bg-secondary-700 text-brand-navy dark:text-secondary-100"
            :group="recipientGroup"
            :dragging="recipientDragging"
            @parse="parseRecipients('to')"
            @drag-start="recipientDragging = true"
            @drag-end="recipientDragging = false"
          />
          <button
            @click="showCc = !showCc"
            class="text-xs font-semibold flex-shrink-0"
            :class="showCc ? 'text-primary-700 dark:text-brand-mint' : 'text-secondary-600 dark:text-secondary-400 hover:text-brand-navy dark:hover:text-white'"
          >
            {{ t('emailComposer.cc') }}
          </button>
          <button
            @click="showBcc = !showBcc"
            class="text-xs font-semibold flex-shrink-0"
            :class="showBcc ? 'text-primary-700 dark:text-brand-mint' : 'text-secondary-600 dark:text-secondary-400 hover:text-brand-navy dark:hover:text-white'"
          >
            {{ t('emailComposer.bcc') }}
          </button>
        </div>
        <p v-if="recipientErrors.to.length" class="px-1 py-1 text-xs text-red-600 dark:text-red-400">
          {{ t('emailComposer.notifications.invalidRecipients', { list: recipientErrors.to.join(', ') }) }}
        </p>

        <!-- CC -->
        <div v-if="showCc || recipientDragging || emailData.cc?.length" class="min-h-10 py-1 flex items-center gap-3 border-b border-secondary-200 dark:border-secondary-700 flex-shrink-0">
          <span :class="fieldLabelClass">{{ t('emailComposer.cc') }}</span>
          <RecipientInput
            v-model="emailData.cc"
            v-model:input-value="emailData.ccInput"
            :placeholder="t('emailComposer.recipientPlaceholder')"
            pill-color-class="bg-secondary-100 dark:bg-secondary-700 text-brand-navy dark:text-secondary-100"
            :group="recipientGroup"
            :dragging="recipientDragging"
            @parse="parseRecipients('cc')"
            @drag-start="recipientDragging = true"
            @drag-end="recipientDragging = false"
          />
        </div>
        <p v-if="recipientErrors.cc.length" class="px-1 py-1 text-xs text-red-600 dark:text-red-400">
          {{ t('emailComposer.notifications.invalidRecipients', { list: recipientErrors.cc.join(', ') }) }}
        </p>

        <!-- BCC -->
        <div v-if="showBcc || recipientDragging || emailData.bcc?.length" class="min-h-10 py-1 flex items-center gap-3 border-b border-secondary-200 dark:border-secondary-700 flex-shrink-0">
          <span :class="fieldLabelClass">{{ t('emailComposer.bcc') }}</span>
          <RecipientInput
            v-model="emailData.bcc"
            v-model:input-value="emailData.bccInput"
            :placeholder="t('emailComposer.recipientPlaceholder')"
            pill-color-class="bg-secondary-100 dark:bg-secondary-700 text-brand-navy dark:text-secondary-100"
            :group="recipientGroup"
            :dragging="recipientDragging"
            @parse="parseRecipients('bcc')"
            @drag-start="recipientDragging = true"
            @drag-end="recipientDragging = false"
          />
        </div>
        <p v-if="recipientErrors.bcc.length" class="px-1 py-1 text-xs text-red-600 dark:text-red-400">
          {{ t('emailComposer.notifications.invalidRecipients', { list: recipientErrors.bcc.join(', ') }) }}
        </p>

        <!-- Asunto -->
        <div class="min-h-10 py-1 flex items-center gap-3 border-b border-secondary-200 dark:border-secondary-700 flex-shrink-0">
          <span :class="fieldLabelClass">{{ t('emailComposer.subject') }}</span>
          <input
            v-model="emailData.subject"
            type="text"
            class="flex-1 min-w-0 h-8 px-0 border-0 bg-transparent text-sm font-semibold text-brand-navy dark:text-white placeholder:text-secondary-400 placeholder:font-normal focus:ring-0 focus:outline-none"
            :placeholder="t('emailComposer.subjectPlaceholder')"
          />
        </div>

        <!-- Plantilla sobre una respuesta/reenvío: el usuario escoge qué asunto sale.
             Grupo SKY perdía sus consecutivos porque la plantilla pisaba el del hilo. -->
        <div
          v-if="threadSubject && templateSubject && threadSubject !== templateSubject"
          class="min-h-8 py-1 flex items-center gap-2 border-b border-secondary-200 dark:border-secondary-700 flex-shrink-0 text-xs"
        >
          <span class="text-secondary-500 dark:text-secondary-400">{{ t('emailComposer.subjectSource.label') }}</span>
          <button type="button" :class="subjectSourceBtnClass('thread')" :title="threadSubject" @click="setSubjectSource('thread')">
            {{ t('emailComposer.subjectSource.thread') }}
          </button>
          <button type="button" :class="subjectSourceBtnClass('template')" :title="templateSubject" @click="setSubjectSource('template')">
            {{ t('emailComposer.subjectSource.template') }}
          </button>
        </div>

        <!-- Barra de formato -->
        <RichTextToolbar
          :active-formats="activeFormats"
          @toggle="toggle"
          @font-family="setFontFamily"
          @font-size="setFontSize"
          @text-color="setTextColor"
          @highlight="setHighlight"
          @clear-format="clearFormat"
          :read-link-context="readLinkContext"
          @link="insertLink($event.url, $event.text)"
          @unlink="removeLink"
        />

        <!-- Editor -->
        <div ref="editorWrapperRef" class="relative flex flex-col" :class="inline ? 'flex-1 min-h-[220px]' : ''">
          <div
            ref="editorRef"
            contenteditable="true"
            role="textbox"
            aria-multiline="true"
            aria-autocomplete="list"
            :aria-controls="mentionOpen ? mentionListId : undefined"
            :aria-activedescendant="mentionOpen && mentionCandidates.length ? `${mentionListId}-${mentionIndex}` : undefined"
            @input="updateBody(); onMentionInput()"
            @keydown="onMentionKeydown"
            @blur="closeMentions"
            @paste="handlePaste"
            @click="selectImageAt"
            @drop="handleDrop"
            @dragover="handleDragOver"
            @dragenter="handleDragEnter"
            @dragleave="handleDragLeave"
            class="rich-text-content flex-1 overflow-y-auto px-1 py-4 focus:outline-none transition-colors bg-white dark:bg-secondary-800 text-brand-navy dark:text-white text-[13px] leading-[21px] rounded-lg"
            :class="[
              inline ? 'min-h-[220px]' : 'min-h-[300px] max-h-[400px]',
              { 'text-secondary-400 dark:text-secondary-500': !emailData.body },
              { 'ring-2 ring-kila-mint bg-kila-mint-subtle': isDragging }
            ]"
          >
          </div>

          <!-- Menciones con @: el mousedown.prevent deja el foco y el cursor en el editor -->
          <Teleport to="body">
            <ul
              v-if="mentionOpen"
              :id="mentionListId"
              role="listbox"
              :aria-label="t('emailComposer.mentions.label')"
              :style="mentionMenuStyle"
              class="fixed w-72 max-h-64 overflow-y-auto py-1 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-xl shadow-xl z-[60]"
            >
              <li
                v-for="(person, index) in mentionCandidates"
                :id="`${mentionListId}-${index}`"
                :key="person.email"
                role="option"
                :aria-selected="index === mentionIndex"
                class="px-3 py-2 cursor-pointer text-[13px] transition-colors"
                :class="index === mentionIndex ? 'bg-secondary-100 dark:bg-secondary-700' : 'hover:bg-secondary-50 dark:hover:bg-secondary-700'"
                @mousedown.prevent="selectMention(person)"
                @mouseenter="mentionIndex = index"
              >
                <span class="block truncate font-medium text-brand-navy dark:text-white">{{ person.name }}</span>
                <span class="block truncate text-xs text-secondary-500 dark:text-secondary-400">{{ person.email }}</span>
              </li>
              <li v-if="mentionCandidates.length === 0" class="px-3 py-2 text-xs text-secondary-500 dark:text-secondary-400">
                {{ t('emailComposer.mentions.empty') }}
              </li>
            </ul>
          </Teleport>

          <!-- Marco y manijas de la imagen seleccionada: se arrastra una esquina
               para cambiarle el tamaño, manteniendo la proporción. -->
          <div
            v-if="imageOverlay"
            data-image-resizer
            class="absolute z-10 pointer-events-none border-2 border-kila-mint rounded-sm"
            :style="{
              left: `${imageOverlay.left}px`,
              top: `${imageOverlay.top}px`,
              width: `${imageOverlay.width}px`,
              height: `${imageOverlay.height}px`
            }"
          >
            <span
              v-for="corner in IMAGE_RESIZE_CORNERS"
              :key="corner.name"
              data-image-resizer
              class="absolute w-2.5 h-2.5 -m-1.5 rounded-sm bg-white border-2 border-kila-mint pointer-events-auto"
              :class="corner.class"
              @mousedown="startResize($event, corner.name)"
            ></span>
            <span
              v-if="isResizingImage"
              class="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-brand-navy/90 text-white text-[11px] whitespace-nowrap"
            >
              {{ imageSize.width }} × {{ imageSize.height }} px
            </span>
          </div>

          <p v-if="isDragging" class="text-sm text-primary-700 dark:text-brand-mint mt-2 text-center">
            {{ t('emailComposer.dropFiles') }}
          </p>
        </div>

        <!-- Adjuntos -->
        <div
          v-if="(emailData.attachments && emailData.attachments.length > 0) || inheritedAttachments.length > 0 || attachedEmails.length > 0 || isResolvingAutoAttachments || autoAttachMissingTypeIds.length > 0"
          ref="attachmentsSectionRef"
          class="py-3 border-t border-secondary-200 dark:border-secondary-700 flex flex-col gap-2 rounded-lg transition-all duration-300 flex-shrink-0"
          :class="highlightAttachments ? 'ring-2 ring-kila-mint ring-offset-4 ring-offset-white dark:ring-offset-secondary-800' : ''"
        >
          <div v-if="totalAttachmentsCount > 0" class="flex items-center gap-2">
            <h4 class="text-xs font-bold text-secondary-600 dark:text-secondary-300">{{ t('emailComposer.attachments', { count: totalAttachmentsCount }) }}</h4>
            <!-- El tope mide lo que sube el usuario: los del original no pasan
                 por el navegador, así que mostrarlos aquí daría un cupo falso. -->
            <span
              v-if="emailData.attachments && emailData.attachments.length > 0"
              class="text-[11px]"
              :class="currentAttachmentsSize > MAX_TOTAL_SIZE * 0.8
                ? 'text-red-500 font-semibold'
                : 'text-secondary-400 dark:text-secondary-500'"
            >
              {{ formatFileSize(currentAttachmentsSize) }} / {{ formatFileSize(MAX_TOTAL_SIZE) }}
            </span>
            <span v-if="isResolvingAutoAttachments" class="text-[11px] text-secondary-400">· {{ t('emailComposer.autoAttach.resolving') }}</span>
          </div>
          <div class="flex items-center gap-2 flex-wrap">
            <!-- Correos de Kila adjuntados por referencia: el backend baja su
                 MIME al enviar, así que no pasan por el navegador. -->
            <div
              v-for="(email, index) in attachedEmails"
              :key="`attached-email-${email.emailId}`"
              class="group h-11 pl-2 pr-1.5 flex items-center gap-2.5 rounded-lg border border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800"
            >
              <div class="w-7 h-7 rounded-md bg-secondary-500 flex items-center justify-center text-white flex-shrink-0">
                <Mail class="w-4 h-4" />
              </div>
              <div class="min-w-0 max-w-[220px] leading-tight">
                <p class="text-xs font-semibold text-brand-navy dark:text-secondary-100 truncate" :title="`${email.subject}.eml`">{{ email.subject }}.eml</p>
                <p class="text-[11px] text-secondary-400">
                  <template v-if="email.estimatedSize">≈ {{ formatFileSize(email.estimatedSize) }} · </template>{{ t('emailComposer.asAttachment.chipHint') }}
                </p>
              </div>
              <button
                @click="removeAttachedEmail(index)"
                class="w-7 h-7 rounded flex items-center justify-center text-secondary-400 hover:bg-secondary-100 dark:hover:bg-secondary-700 hover:text-red-500 transition-colors"
              >
                <X class="w-3.5 h-3.5" />
              </button>
            </div>
            <!-- Adjuntos que vienen del correo original. Se listan para que el
                 usuario sepa que van: los copia el proveedor, no el navegador. -->
            <div
              v-for="(file, index) in inheritedAttachments"
              :key="`inherited-${file.id}-${index}`"
              class="group h-11 pl-2 pr-1.5 flex items-center gap-2.5 rounded-lg border border-kila-mint/60 bg-kila-mint/5 dark:bg-kila-mint/10"
            >
              <div class="w-7 h-7 rounded-md bg-kila-mint flex items-center justify-center text-brand-navy flex-shrink-0">
                <File class="w-4 h-4" />
              </div>
              <div class="min-w-0 max-w-[220px] leading-tight">
                <p class="text-xs font-semibold text-brand-navy dark:text-secondary-100 truncate" :title="file.name">{{ file.name }}</p>
                <p class="text-[11px] text-secondary-400">{{ formatFileSize(file.size) }} · {{ t('emailComposer.attachmentFromOriginal') }}</p>
              </div>
              <button
                @click="removeInheritedAttachment(index)"
                :title="t('emailComposer.removeFromForward')"
                class="w-7 h-7 rounded flex items-center justify-center text-secondary-400 hover:bg-secondary-100 dark:hover:bg-secondary-700 hover:text-red-500 transition-colors"
              >
                <X class="w-3.5 h-3.5" />
              </button>
            </div>
            <div
              v-for="(file, index) in emailData.attachments"
              :key="index"
              class="group h-11 pl-2 pr-1.5 flex items-center gap-2.5 rounded-lg border border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800"
            >
              <div class="w-7 h-7 rounded-md bg-secondary-500 flex items-center justify-center text-white flex-shrink-0">
                <File class="w-4 h-4" />
              </div>
              <div class="min-w-0 max-w-[220px] leading-tight">
                <p class="text-xs font-semibold text-brand-navy dark:text-secondary-100 truncate" :title="file.name">{{ file.name }}</p>
                <p class="text-[11px] text-secondary-400">{{ formatFileSize(file.size) }}</p>
              </div>
              <button
                v-if="canPreviewFile(file)"
                type="button"
                @click="openPreview(file)"
                :title="t('emailWorkspace.reading.preview')"
                :aria-label="t('emailWorkspace.reading.preview')"
                class="w-7 h-7 rounded flex items-center justify-center text-secondary-400 hover:bg-secondary-100 dark:hover:bg-secondary-700 hover:text-brand-navy dark:hover:text-white transition-colors"
              >
                <Eye class="w-3.5 h-3.5" />
              </button>
              <button
                @click="removeAttachment(index)"
                class="w-7 h-7 rounded flex items-center justify-center text-secondary-400 hover:bg-secondary-100 dark:hover:bg-secondary-700 hover:text-red-500 transition-colors"
              >
                <X class="w-3.5 h-3.5" />
              </button>
            </div>
            <div
              v-if="autoAttachMissingTypeIds.length > 0"
              class="h-11 px-3 flex items-center gap-1.5 rounded-lg border border-dashed border-amber-400 text-xs font-semibold text-amber-600 dark:text-amber-400"
            >
              <AlertTriangle class="w-3.5 h-3.5 flex-shrink-0" />
              {{ t('emailComposer.autoAttach.missing') }}
              {{ autoAttachMissingTypeIds.map(id => getDocTypeName(id, locale)).join(', ') }}
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>

    <!-- Discard Confirmation Modal -->
    <ConfirmModal
      :is-open="showDiscardModal"
      :title="t('emailComposer.discard.title')"
      :message="t('emailComposer.discard.message')"
      :confirm-text="t('emailComposer.discard.confirm')"
      :cancel-text="t('emailComposer.discard.cancel')"
      variant="warning"
      @confirm="confirmDiscard"
      @cancel="cancelDiscard"
    />

    <!-- Template Editor Modal -->
    <TemplateEditor
      v-if="showTemplateEditor"
      @close="showTemplateEditor = false"
      @saved="onTemplateSaved"
    />

    <!-- Signature Manager Modal -->
    <SignatureManager
      v-if="showSignatureManager"
      @close="showSignatureManager = false"
    />

    <!-- Document Attachment Picker Modal -->
    <DocumentAttachmentPicker
      v-if="showDocumentPicker && props.attachmentSource"
      :source="props.attachmentSource"
      :current-attachments-size="currentAttachmentsSize"
      @close="showDocumentPicker = false"
      @attach="handleDocumentPickerAttach"
    />

    <!-- Vista previa de un adjunto local: el visor del buzón, sin enviarlo ni
         descargarlo. El key fuerza un visor nuevo si se abre otro archivo. -->
    <AttachmentPreview
      v-if="previewAttachment"
      :key="previewAttachment.downloadUrl"
      :attachment="previewAttachment"
      :email-id="props.emailId ?? ''"
      read-only
      @close="closePreview"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount, type Ref } from 'vue';
import { emailService } from '@/services';
import type { EmailDraft, EmailDraftInput } from '@/services/emailService';
import { useI18n } from 'vue-i18n';
import {
  X, Paperclip, File, Send, ChevronDown, Plus, Lock,
  HardDrive, Ship, ShoppingCart, Trash2, LayoutTemplate, PenLine, Star, AlertTriangle, Check,
  Minus, Maximize2, Minimize2, ExternalLink, Mail, Eye
} from 'lucide-vue-next';
import { useEmailStore, useUiStore, useEmailAuthStore } from '@/stores';
import type { ComposerWindowState } from '@/stores/ui';
import { useCompanyStore } from '@/stores/company';
import { useAuthStore } from '@/stores/auth';
import { sanitizeHtml } from '@/utils';
import { useRichTextFormat } from '@/composables/useRichTextFormat';
import RichTextToolbar from './editor/RichTextToolbar.vue';
import { useEmailTemplateStore } from '@/stores/emailTemplate';
import { useEmailSignatureStore } from '@/stores/emailSignature';
import { useTemplateResolver } from '@/composables/useTemplateResolver';
import { usePermissions } from '@/composables/usePermissions';
import { useTemplateTrigger, type TemplateTriggerModule } from '@/composables/useTemplateTrigger';
import { useEmailAssociation } from '@/composables/useEmailAssociation';
import { useEmails } from '@/composables/useEmails';
import { useNotification } from '@/composables/useNotification';
import { parseRecipientInput, parseSingleAddress, ensureLeadingBlankLine, inheritedForwardAttachments, cidImagesToUrls, urlsToCidImages, pasteSanitizedHtml, sanitizeEmailHtml } from '@/utils/email';
import { buildReplySubject, buildForwardSubject } from '@/utils/emailSubject';
import { formatQuoteAddress, formatQuoteAddressList, formatQuoteDate, buildQuotedMessage, splitAtQuote } from '@/utils/emailQuote';
import type { InheritedAttachment } from '@/types';
import ConfirmModal from '@/components/common/ConfirmModal.vue';
import TemplateEditor from '@/components/email/templates/TemplateEditor.vue';
import SignatureManager from '@/components/email/signatures/SignatureManager.vue';
import RecipientInput from '@/components/email/RecipientInput.vue';
import { useEditorImageResize, IMAGE_RESIZE_CORNERS } from '@/composables/useEditorImageResize';
import { useMentions, extractMentionEmails, addToRecipients } from '@/composables/useMentions';
import DocumentAttachmentPicker from '@/components/email/DocumentAttachmentPicker.vue';
import type { AttachmentSource } from '@/components/email/DocumentAttachmentPicker.vue';
import AttachmentPreview from '@/components/email/AttachmentPreview.vue';
import attachmentService from '@/services/attachmentService';
import { resolveMimeType } from '@/utils/mimeType';
import type { EmailAddress, EmailAttachment, EmailComposer as EmailComposerType, AttachedEmailRef } from '@/types';
import type { EmailTemplate, TemplateDataContext } from '@/types/emailTemplate';
import type { EmailSignature } from '@/types/emailSignature';
import { SIGNATURE_WRAPPER_CLASS } from '@/types/emailSignature';
import { ShipmentService } from '@/application/services/ShipmentService';
import { FirestoreShipmentRepository } from '@/infrastructure/repositories/FirestoreShipmentRepository';
import { usePurchaseOrderDocuments } from '@/composables/usePurchaseOrderDocuments';
import { useDocumentTypes } from '@/composables/useDocumentTypes';
import { resolveAutoAttachments, type AttachableFile } from '@/utils/templateAutoAttach';
import { getFileDownloadName } from '@/utils/shipmentFileDisplayName';
import { getStorage, ref as storageRef, getBlob } from 'firebase/storage';
import { snapshotFiles } from '@/utils/fileSnapshot';
import {
  EMAIL_ATTACHMENT_TOTAL_LIMIT_BYTES,
  EMAIL_ATTACHMENT_TOTAL_LIMIT_MB,
  MAX_ATTACHED_EMAILS,
} from '@/config/emailAttachmentLimits';

import { formatDate as coreFormatDate } from '@/composables/useFormat';
const { t, locale } = useI18n();
const emailStore = useEmailStore();
const emailAuthStore = useEmailAuthStore();
const uiStore = useUiStore();
const companyStore = useCompanyStore();
const authStore = useAuthStore();
const templateStore = useEmailTemplateStore();
const signatureStore = useEmailSignatureStore();
const { resolveTemplate } = useTemplateResolver();
const { applyTemplateTrigger, applyAutoFillDateField } = useTemplateTrigger();
const { hasPermission } = usePermissions();
const { associateReplyToContext, associateNewEmailToContext } = useEmailAssociation();
const { fetchEmailById: fetchFirestoreEmailById } = useEmails();
const { showNotification } = useNotification();
const { getDocumentTypeName: getDocTypeName, ensureLoaded: ensureDocTypesLoaded } = useDocumentTypes();

const activeTemplateId = ref<string | null>(null);
const activeTemplateName = ref<string | null>(null);
const activeTemplateData = ref<EmailTemplate | null>(null);

const templateAutoAttachments = ref<File[]>([]);
const isResolvingAutoAttachments = ref(false);
const autoAttachMissingTypeIds = ref<string[]>([]);
let autoAttachGeneration = 0;

// Solo se limita el peso total del correo, nunca el de cada archivo ni la cantidad
const MAX_TOTAL_SIZE = EMAIL_ATTACHMENT_TOTAL_LIMIT_BYTES;

const props = withDefaults(defineProps<{
  /** Identifica la sesión en uiStore: cierre, foco, colas de adjuntos pendientes */
  sessionId: number;
  mode: 'new' | 'reply' | 'replyAll' | 'forward';
  emailId?: string;
  initialTemplate?: EmailTemplate | null;
  templateContext?: TemplateDataContext;
  attachmentSource?: AttachmentSource;
  /** Borrador de Kila con el que se retoma la redacción */
  draft?: EmailDraft | null;
  /** Dibujado dentro del panel de lectura en vez de como ventana flotante */
  inline?: boolean;
  /** Abre el desplegable de plantillas al montar ("Responder con plantilla") */
  openTemplatesOnMount?: boolean;
  /** Solo aplica cuando no es `inline` */
  windowState?: ComposerWindowState;
  narrow?: boolean;
  contextModule?: 'shipment' | 'purchaseOrder';
  contextEntityId?: string;
  contextEntityIdNumber?: string;
  /** Asunto con el que arranca un correo nuevo (p.ej. "Reenviar como adjunto" sin compositores abiertos) */
  initialSubject?: string;
  /** Destinatarios/cuerpo con los que arranca un correo nuevo: respuesta a un correo adjunto (.eml) sin hilo real */
  initialTo?: EmailAddress[];
  initialCc?: EmailAddress[];
  initialBodyHtml?: string;
  /** Cabeceras de hilo para que Gmail (Microsoft las ignora sin error) enganche la respuesta a un correo embebido */
  inReplyTo?: string;
  references?: string;
}>(), {
  windowState: 'open',
});

const fieldLabelClass = 'h-7 min-w-[56px] px-2.5 rounded-md bg-secondary-100 dark:bg-secondary-700 text-xs font-semibold text-secondary-600 dark:text-secondary-300 flex items-center justify-center flex-shrink-0';
/** Nombre de la última plantilla aplicada, para mostrarlo en el botón como en el diseño */
const appliedTemplateName = ref<string>('');

const showTemplateDropdown = ref(false);
const templateDropdownRef = ref<HTMLElement | null>(null);

// Sin permiso de gestión el usuario solo escoge plantillas del desplegable
const canManageTemplates = computed(() => hasPermission('emails', 'manage_templates'));
const showTemplateEditor = ref(false);
const showSignatureDropdown = ref(false);
const signatureDropdownRef = ref<HTMLElement | null>(null);
const showSignatureManager = ref(false);
const showAttachmentDropdown = ref(false);
const showPriorityDropdown = ref(false);
const PRIORITY_LEVELS = ['low', 'normal', 'high'] as const;

// Los desplegables se teleportan al body y van en `fixed`: la fila de comandos
// hace scroll horizontal y un `absolute` quedaba recortado detrás de los campos.
const attachmentMenuRef = ref<HTMLElement | null>(null);
const templateMenuRef = ref<HTMLElement | null>(null);
const signatureMenuRef = ref<HTMLElement | null>(null);
const attachmentMenuStyle = ref<Record<string, string>>({});
const templateMenuStyle = ref<Record<string, string>>({});
const signatureMenuStyle = ref<Record<string, string>>({});
const priorityDropdownRef = ref<HTMLElement | null>(null);
const priorityMenuRef = ref<HTMLElement | null>(null);
const priorityMenuStyle = ref<Record<string, string>>({});

function anchorMenu(
  isOpen: Ref<boolean>,
  anchor: Ref<HTMLElement | null>,
  menu: Ref<HTMLElement | null>,
  style: Ref<Record<string, string>>
) {
  watch(isOpen, async (open) => {
    if (!open) return;
    const rect = anchor.value?.getBoundingClientRect();
    if (!rect) return;
    style.value = { top: `${rect.bottom + 4}px`, left: `${rect.left}px` };
    await nextTick();
    const width = menu.value?.offsetWidth || 0;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    style.value = { top: `${rect.bottom + 4}px`, left: `${left}px` };
  });
}
const showDocumentPicker = ref(false);
const attachmentDropdownRef = ref<HTMLElement | null>(null);

anchorMenu(showAttachmentDropdown, attachmentDropdownRef, attachmentMenuRef, attachmentMenuStyle);
anchorMenu(showTemplateDropdown, templateDropdownRef, templateMenuRef, templateMenuStyle);
anchorMenu(showSignatureDropdown, signatureDropdownRef, signatureMenuRef, signatureMenuStyle);
anchorMenu(showPriorityDropdown, priorityDropdownRef, priorityMenuRef, priorityMenuStyle);

const editorRef = ref<HTMLElement | null>(null);
const editorWrapperRef = ref<HTMLElement | null>(null);

const {
  imageOverlay,
  isResizingImage,
  imageSize,
  fitNewImages,
  selectImageAt,
  startResize,
  clearImageSelection,
} = useEditorImageResize(editorRef, editorWrapperRef, () => updateBody());

// Remitente y destinatarios del correo original (reply/forward): también se pueden mencionar
const originalParticipants = ref<EmailAddress[]>([]);
const mentionListId = computed(() => `composer-mentions-${props.sessionId}`);
const {
  isOpen: mentionOpen,
  candidates: mentionCandidates,
  highlightedIndex: mentionIndex,
  menuStyle: mentionMenuStyle,
  onInput: onMentionInput,
  onKeydown: onMentionKeydown,
  select: selectMention,
  close: closeMentions,
} = useMentions(editorRef, {
  participants: () => [
    ...emailData.value.to,
    ...(emailData.value.cc || []),
    ...(emailData.value.bcc || []),
    ...originalParticipants.value,
  ],
  // Como Outlook: el mencionado pasa a Para si no estaba en ningún campo.
  // Borrar la mención después no lo quita de Para.
  onSelect: (person) => {
    const to = addToRecipients(emailData.value, person);
    if (to) emailData.value.to = to;
  },
  onChange: () => updateBody(),
});
const attachmentsSectionRef = ref<HTMLElement | null>(null);

// Adjuntos del correo original en un reenvío: los arrastra el proveedor y nunca
// pasan por el navegador, así que solo se listan para que el usuario los vea.
// Lo que quita pasa a `excludedAttachments` y viaja para que el backend lo
// descarte; antes la ventana se abría vacía y los volvían a subir a mano.
const inheritedAttachments = ref<InheritedAttachment[]>([]);
const excludedAttachments = ref<InheritedAttachment[]>([]);
// Correos de Kila adjuntados por referencia: "Reenviar como adjunto" desde el
// hilo, o desde la cola de la sesión (ver el watch de sessionState más abajo).
const attachedEmails = ref<AttachedEmailRef[]>([]);
const highlightAttachments = ref(false);
let highlightTimer: ReturnType<typeof setTimeout> | null = null;
const showCc = ref(false);
const showBcc = ref(false);
// Hay una ficha de destinatario en el aire: CC y CCO se ven para tener dónde soltarla,
// sin tocar showCc/showBcc (que son lo que el usuario abrió con los botones).
const recipientDragging = ref(false);
// Hasta tres redactores conviven en pantalla: cada uno con su grupo, para que no se
// pasen destinatarios de un correo a otro.
const recipientGroup = computed(() => `composer-recipients-${props.sessionId}`);
const isSending = ref(false);
const isSaving = ref(false);
const isDragging = ref(false);
let dragCounter = 0;

// Fragmentos que no pasaron la validación en cada campo, para señalarlos debajo.
const recipientErrors = ref<{ to: string[]; cc: string[]; bcc: string[] }>({ to: [], cc: [], bcc: [] });

const emailData = ref<EmailComposerType & { toInput: string; ccInput: string; bccInput: string }>({
  to: [],
  cc: [],
  bcc: [],
  subject: '',
  body: '',
  attachments: [],
  importance: 'normal',
  toInput: '',
  ccInput: '',
  bccInput: '',
});

const composerTitle = computed(() => {
  switch (props.mode) {
    case 'reply':
      return t('emailComposer.titles.reply');
    case 'replyAll':
      return t('emailComposer.titles.replyAll');
    case 'forward':
      return t('emailComposer.titles.forward');
    default:
      return t('emailComposer.titles.new');
  }
});

// ── Ventana flotante: tamaño de la raíz y el panel, y controles de la
// barra de título. `inline` conserva las clases del rediseño del 3-sep;
// nunca se le pone transform/translate a la raíz ni al panel (rompería el
// muelle de GlobalEmailComposer.vue). ──────────────────────────────────
const rootClass = computed(() => {
  if (props.inline) return 'h-full w-full flex flex-col bg-white dark:bg-secondary-800';
  if (props.windowState === 'maximized') {
    return 'pointer-events-auto fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4';
  }
  const size = props.windowState === 'minimized'
    ? (props.narrow ? 'w-44' : 'w-56')
    : (props.narrow ? 'w-[calc(100vw-1rem)] h-[calc(100vh-80px)]' : 'w-[520px] h-[min(600px,calc(100vh-88px))]');
  return `pointer-events-auto flex-shrink-0 ${size}`;
});

const panelClass = computed(() => {
  if (props.inline) return 'flex-1 min-h-0 w-full flex flex-col';
  if (props.windowState === 'maximized') {
    return 'bg-white dark:bg-secondary-800 rounded-xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden';
  }
  return 'bg-white dark:bg-secondary-800 rounded-t-xl shadow-2xl border border-secondary-200 dark:border-secondary-700 w-full h-full flex flex-col overflow-hidden';
});

const windowTitle = computed(() => emailData.value.subject.trim() || t('emailComposer.window.newMessage'));

function setWindow(state: ComposerWindowState) {
  uiStore.setSessionWindow(props.sessionId, state);
  if (state === 'minimized') {
    showAttachmentDropdown.value = false;
    showTemplateDropdown.value = false;
    showSignatureDropdown.value = false;
    showPriorityDropdown.value = false;
    clearImageSelection();
  }
}

function toggleMinimize() {
  if (props.windowState === 'minimized') {
    uiStore.focusSession(props.sessionId);
  } else {
    setWindow('minimized');
  }
}

function toggleMaximize() {
  setWindow(props.windowState === 'maximized' ? 'open' : 'maximized');
}

// ── Asunto del hilo vs. asunto de la plantilla ───────────────────────────
// Al aplicar una plantilla en una respuesta/reenvío se guardan los dos y el
// usuario elige con el selector bajo el asunto. La última elección se recuerda
// en este navegador: un cliente conserva siempre sus consecutivos, otro quiere
// siempre el asunto de la plantilla, y ninguno tiene que decidirlo cada vez.
type SubjectSource = 'thread' | 'template';
const SUBJECT_SOURCE_KEY = 'emailComposer.subjectSource';
const threadSubject = ref<string | null>(null);
const templateSubject = ref<string | null>(null);
const subjectSource = ref<SubjectSource>(readSubjectSourcePreference());

function readSubjectSourcePreference(): SubjectSource {
  try {
    return localStorage.getItem(SUBJECT_SOURCE_KEY) === 'thread' ? 'thread' : 'template';
  } catch {
    return 'template';
  }
}

function setSubjectSource(source: SubjectSource) {
  subjectSource.value = source;
  const chosen = source === 'thread' ? threadSubject.value : templateSubject.value;
  if (chosen) emailData.value.subject = chosen;
  try {
    localStorage.setItem(SUBJECT_SOURCE_KEY, source);
  } catch {
    // sin storage no se recuerda la elección; el selector sigue funcionando
  }
}

function subjectSourceBtnClass(source: SubjectSource) {
  return [
    'h-6 px-2.5 rounded-full border font-medium transition-colors',
    subjectSource.value === source
      ? 'border-kila-mint bg-kila-mint-subtle text-primary-700 dark:text-brand-mint'
      : 'border-secondary-200 dark:border-secondary-700 text-secondary-600 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700',
  ];
}

// Un destinatario recién escrito cuenta aunque todavía no sea ficha: la ficha
// se crea 150 ms después del blur y el clic en Enviar llegaba antes, con el
// botón aún deshabilitado. sendEmail lo convierte (o lo rechaza) al enviar.
const canSend = computed(() => {
  return (emailData.value.to.length > 0 || emailData.value.toInput.trim() !== '') &&
         emailData.value.subject.trim() !== '' && 
         emailData.value.body.trim() !== '';
});

// Mapa URL de Storage -> cid: de las imágenes del original resueltas para el
// editor (ver cidImagesToUrls). urlsToCidImages lo usa justo antes de enviar.
let cidByUrl: Record<string, string> = {};

// Cargar datos del correo original para reply/replyAll/forward
async function loadOriginalEmail() {
  if (!props.emailId) return;

  // Buscar el email en Firestore (fuente de verdad) con fallback al store de Microsoft Graph
  let originalEmail: any = await fetchFirestoreEmailById(props.emailId);
  if (!originalEmail) {
    await emailStore.fetchEmailById(props.emailId);
    originalEmail = emailStore.currentEmail;
  }

  if (!originalEmail) return;

  // Compatibilidad con EmailComplete (Firestore: fromAddress, bodyHtml, receivedAt)
  // y Email (Microsoft Graph: from, body.content, receivedDateTime)
  const rawFrom = originalEmail.fromAddress || originalEmail.from?.email || t('emailComposer.quoted.unknown');
  const { name: fromName, email: fromEmail } = parseAddress(rawFrom, originalEmail.from?.name);
  const ccAddresses = originalEmail.ccAddresses || (originalEmail.cc || []).map((c: any) => c.email || c);
  const bodyContent = originalEmail.bodyHtml || originalEmail.bodyText || originalEmail.body?.content || '';
  const rawDate = originalEmail.receivedAt || originalEmail.receivedDateTime;
  const receivedDate = rawDate instanceof Date
    ? rawDate
    : rawDate?.toDate?.() || new Date(rawDate);

  // La bandeja de Kila mezcla recibidos y enviados (la suscripción es sobre
  // me/messages), así que es muy común responder sobre un correo PROPIO. En ese
  // caso el destinatario no es uno mismo sino a quienes iba el original, como
  // hace Outlook. La propia dirección nunca se agrega como destinatario.
  const ownAddress = (emailAuthStore.userEmail || '').toLowerCase();
  const isOwnMessage = !!ownAddress && fromEmail.toLowerCase() === ownAddress;
  const sender = { email: fromEmail, name: fromName || fromEmail };
  const toList: string[] = originalEmail.toAddresses || (originalEmail.to || []).map((t: any) => t.email || t);
  originalParticipants.value = [sender, ...buildRecipients([...toList, ...ccAddresses], [fromEmail])];

  // Cita del historial estilo Outlook: línea gris fina y bloque De/Enviado el/
  // Para/CC/Asunto en negrita. Las cid: del original se resuelven a URL de
  // Storage para que se vean en el editor; cidByUrl las vuelve a cid: justo
  // antes de enviar (ver sendEmail), porque el proveedor no entiende esas URL.
  const toQuote = formatQuoteAddressList(toList.map(a => parseSingleAddress(a) ?? { email: a }));
  const ccQuote = formatQuoteAddressList(ccAddresses.map((a: string) => parseSingleAddress(a) ?? { email: a }));
  const fromQuote = formatQuoteAddress({ name: fromName, email: fromEmail });
  const { html: displayBody, urlToCid } = cidImagesToUrls(bodyContent, originalEmail.attachments || []);
  cidByUrl = urlToCid;
  const quotedMessage = buildQuotedMessage(
    {
      from: fromQuote,
      sent: formatQuoteDate(receivedDate, locale.value),
      to: toQuote,
      cc: ccQuote,
      subject: originalEmail.subject || '',
    },
    {
      from: t('emailComposer.quoted.from'),
      sent: t('emailComposer.quoted.sent'),
      to: t('emailComposer.quoted.to'),
      cc: t('emailComposer.quoted.cc'),
      subject: t('emailComposer.quoted.subject'),
    },
    sanitizeHtml(displayBody)
  );

  if (props.mode === 'reply') {
    const to = isOwnMessage ? buildRecipients(toList, [ownAddress]) : [sender];
    emailData.value.to = to.length > 0 ? to : [sender];
    emailData.value.subject = buildReplySubject(originalEmail.subject);
    emailData.value.body = '<div><br></div>' + quotedMessage;
  } else if (props.mode === 'replyAll') {
    let to: EmailAddress[];
    let cc: EmailAddress[];
    if (isOwnMessage) {
      // Se conserva la distribución original: Para sigue en Para y CC en CC
      to = buildRecipients(toList, [ownAddress]);
      cc = buildRecipients(ccAddresses, [ownAddress, ...to.map(r => r.email)]);
    } else {
      // Como Outlook: el remitente y los destinatarios originales van juntos en
      // Para, y solo los que venían en copia siguen en copia. Antes todo el
      // hilo menos el remitente caía en CC y había que reorganizarlo a mano
      // (reporte de Partequipos Repuestos).
      to = [sender, ...buildRecipients(toList, [ownAddress, fromEmail])];
      cc = buildRecipients(ccAddresses, [ownAddress, ...to.map(r => r.email)]);
    }
    emailData.value.to = to.length > 0 ? to : [sender];
    emailData.value.cc = cc;
    showCc.value = cc.length > 0;

    emailData.value.subject = buildReplySubject(originalEmail.subject);
    emailData.value.body = '<div><br></div>' + quotedMessage;
  } else if (props.mode === 'forward') {
    emailData.value.subject = buildForwardSubject(originalEmail.subject, locale.value);
    emailData.value.body = '<div><br></div>' + quotedMessage;
    inheritedAttachments.value = inheritedForwardAttachments(originalEmail);
    // Un correo enviado desde Kila queda como copia provisional (sentByApp,
    // attachments: []) hasta que la sincronización lo reemplaza. El reenvío sí
    // lleva sus adjuntos —el backend resuelve el mensaje real—, así que se le
    // piden al proveedor para que la ventana no se abra vacía (Partequipos).
    if (originalEmail.sentByApp && inheritedAttachments.value.length === 0) {
      const live = await emailService.getEmailById(props.emailId).catch(() => null);
      inheritedAttachments.value = inheritedForwardAttachments(live);
    }
  }

  // Asunto del hilo, para poder volver a él si una plantilla lo reemplaza
  threadSubject.value = emailData.value.subject || null;

  renderEditor(emailData.value.body);
}

/**
 * Convierte las direcciones crudas del correo original ("mail" o "Nombre <mail>")
 * en fichas, sin repetidas y sin las excluidas. La comparación es por correo en
 * minúsculas: antes se comparaba el texto crudo y "Nombre <mail>" nunca coincidía
 * con "mail", así que el remitente podía quedar también en CC.
 */
function buildRecipients(raw: string[], excluded: string[]): EmailAddress[] {
  const skip = new Set(excluded.map(e => e.toLowerCase()).filter(Boolean));
  const result: EmailAddress[] = [];
  for (const addr of raw) {
    const parsed = parseAddress(addr);
    const key = parsed.email.toLowerCase();
    if (!key || skip.has(key)) continue;
    skip.add(key);
    result.push({ email: parsed.email, name: parsed.name || parsed.email });
  }
  return result;
}

// Parsear dirección de email con formato "Nombre <email>" o solo "email"
function parseAddress(raw: string, fallbackName?: string): { name: string; email: string } {
  const match = raw.match(/^(.+?)\s*<(.+?)>$/);
  if (match) {
    return { name: match[1].trim(), email: match[2].trim() };
  }
  return { name: fallbackName || '', email: raw.trim() };
}

function parseRecipients(field: 'to' | 'cc' | 'bcc') {
  const inputField = `${field}Input` as 'toInput' | 'ccInput' | 'bccInput';
  const input = emailData.value[inputField];
  
  if (!input.trim()) return;

  const { valid, invalid } = parseRecipientInput(input);
  if (valid.length > 0) {
    if (!emailData.value[field]) {
      emailData.value[field] = [];
    }
    const existing = new Set((emailData.value[field] || []).map(r => r.email.toLowerCase()));
    // Arreglo nuevo, no push: la lista de arrastre solo recalcula sus índices cuando cambia la referencia
    emailData.value[field] = [...(emailData.value[field] || []), ...valid.filter(r => !existing.has(r.email.toLowerCase()))];
  }

  // Lo que no es una dirección válida se queda en el campo para corregirlo,
  // en vez de desaparecer sin aviso, y se señala debajo del campo.
  emailData.value[inputField] = invalid.join('; ');
  recipientErrors.value[field] = invalid;
  if (invalid.length > 0) {
    showNotification(
      'error',
      t('emailComposer.notifications.invalidRecipientsTitle'),
      t('emailComposer.notifications.invalidRecipients', { list: invalid.slice(0, 5).join(', ') })
    );
  }
}

/**
 * Antes de enviar, convierte en fichas lo que quedó escrito en Para/CC/CCO.
 * Devuelve false si algo no es una dirección válida: el correo no sale con
 * un destinatario menos sin que el usuario se entere.
 */
function settlePendingRecipients(): boolean {
  (['to', 'cc', 'bcc'] as const).forEach((field) => parseRecipients(field));
  const pending = (['to', 'cc', 'bcc'] as const).flatMap((field) => recipientErrors.value[field]);
  return pending.length === 0;
}


const {
  activeFormats,
  toggle,
  setFontFamily,
  setFontSize,
  normalizeFontSize,
  setTextColor,
  setHighlight,
  clearFormat,
  insertLink,
  removeLink,
  linkAtSelection,
} = useRichTextFormat(editorRef, updateBody);

/** Lo que el diálogo de enlace muestra al abrirse: el enlace del cursor y el texto elegido. */
function readLinkContext() {
  const link = linkAtSelection();
  return {
    url: link?.getAttribute('href') || '',
    text: link?.textContent || document.getSelection()?.toString() || '',
  };
}

function updateBody() {
  if (editorRef.value) {
    fitNewImages();
    normalizeFontSize();
    emailData.value.body = editorRef.value.innerHTML;
  }
}

function handlePaste(event: ClipboardEvent) {
  if (pasteSanitizedHtml(event)) updateBody();
}

/**
 * Único punto de montaje del contenido en el editor.
 *
 * El body puede incluir la cita del correo externo (reply/forward): se sanitiza
 * SIEMPRE antes de montar en el DOM vivo, porque asignar innerHTML dispara
 * handlers como onerror de <img> aunque la cita ya venga sanitizada aguas
 * arriba (defensa en profundidad).
 */
function renderEditor(html: string, options: { focus?: boolean } = {}) {
  if (!editorRef.value) return;

  editorRef.value.textContent = '';
  const temp = document.createElement('div');
  temp.innerHTML = sanitizeHtml(html);
  while (temp.firstChild) editorRef.value.appendChild(temp.firstChild);

  ensureLeadingBlankLine(editorRef.value);
  clearImageSelection();
  fitNewImages();

  if (options.focus) focusEditorStart();
}

/** Deja el cursor en la primera línea, encima de la firma y de la cita. */
function focusEditorStart() {
  const first = editorRef.value?.firstElementChild;
  if (!editorRef.value || !first) return;

  editorRef.value.focus();
  const range = document.createRange();
  range.setStart(first, 0);
  range.collapse(true);
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}

function openDocumentPicker() {
  showAttachmentDropdown.value = false;
  showDocumentPicker.value = true;
}

function handleDocumentPickerAttach(files: File[], failed: string[]) {
  showDocumentPicker.value = false;
  addAttachments(files, failed);
}

/** Lleva la vista a la lista de adjuntos y la resalta un instante */
async function revealAttachments() {
  await nextTick();
  attachmentsSectionRef.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  highlightAttachments.value = true;
  if (highlightTimer) clearTimeout(highlightTimer);
  highlightTimer = setTimeout(() => { highlightAttachments.value = false; }, 2500);
}

const currentAttachmentsSize = computed(() => {
  if (!emailData.value.attachments) return 0;
  return emailData.value.attachments.reduce((sum, f) => sum + f.size, 0);
});

/** Lo que el usuario cuenta con los ojos: los del original más los suyos. */
const totalAttachmentsCount = computed(
  () => inheritedAttachments.value.length + (emailData.value.attachments?.length || 0) + attachedEmails.value.length
);

async function handleFileSelect(event: Event) {
  const target = event.target as HTMLInputElement;
  if (target.files) {
    await attachLocalFiles(Array.from(target.files));
  }
}

/**
 * Archivos del computador (selector o arrastrar y soltar): se copian a memoria
 * al adjuntar. Si el navegador no puede leer alguno se avisa aquí mismo, en
 * vez de fallar el envío sin explicación (ver utils/fileSnapshot).
 */
async function attachLocalFiles(files: File[]) {
  const { files: snapshots, failed } = await snapshotFiles(files);
  addAttachments(snapshots, failed);
}

/** `failed`: nombres que ni siquiera se pudieron descargar antes de llegar aquí */
function addAttachments(files: File[], failed: string[] = []) {
  if (!emailData.value.attachments) {
    emailData.value.attachments = [];
  }

  const rejected: string[] = [];
  const accepted: File[] = [];
  let runningTotal = currentAttachmentsSize.value;

  for (const file of files) {
    if (runningTotal + file.size > MAX_TOTAL_SIZE) {
      rejected.push(t('emailComposer.notifications.exceedsTotalLimit', { name: file.name, max: EMAIL_ATTACHMENT_TOTAL_LIMIT_MB }));
      continue;
    }
    accepted.push(file);
    runningTotal += file.size;
  }

  if (rejected.length > 0) {
    showNotification('warning', t('emailComposer.notifications.filesRejected'), rejected.join(', '));
  }

  if (accepted.length > 0) {
    emailData.value.attachments.push(...accepted);
    revealAttachments();
  }

  const attachedNames = accepted.map(f => f.name);

  if (failed.length > 0) {
    showNotification(
      'error',
      t('emailComposer.notifications.attachFailed'),
      attachedNames.length > 0
        ? t('emailComposer.notifications.attachPartialMsg', { attached: attachedNames.join(', '), failed: failed.join(', ') })
        : t('emailComposer.notifications.attachNoneMsg', { failed: failed.join(', ') })
    );
  } else if (attachedNames.length > 0) {
    showNotification(
      'success',
      t('emailComposer.notifications.filesAttached'),
      t('emailComposer.notifications.filesAttachedMsg', { list: attachedNames.join(', ') })
    );
  }
}

async function downloadAttachableFile(f: AttachableFile): Promise<File> {
  let blob: Blob;
  if (f.storagePath) {
    const storage = getStorage();
    blob = await getBlob(storageRef(storage, f.storagePath));
  } else {
    const response = await fetch(f.fileUrl);
    if (!response.ok) throw new Error(`Error descargando ${f.fileName}: ${response.status}`);
    blob = await response.blob();
  }
  return new window.File([blob], f.fileName, { type: f.mimeType || blob.type });
}

async function loadEntityFiles(): Promise<AttachableFile[]> {
  const source = props.attachmentSource;
  const companyId = companyStore.selectedCompanyId;
  if (!source || !companyId) return [];

  if (source.type === 'shipment') {
    const service = new ShipmentService(new FirestoreShipmentRepository());
    const files = await service.getShipmentFiles(source.id, companyId);
    // El adjunto lleva el nombre visual (KILA-481), igual que en el selector de
    // documentos, para que el filtro de ya adjuntos (nombre + tamaño) los empate.
    // Se descarga por fileUrl, así que el nombre no interviene en la ruta.
    return files.map(f => ({
      id: f.id, fileName: getFileDownloadName(f), fileUrl: f.fileUrl, fileType: f.fileType,
      fileSize: f.fileSize, mimeType: f.mimeType,
    }));
  }

  const { documents, loadDocuments } = usePurchaseOrderDocuments();
  await loadDocuments(source.id);
  return documents.value.map(d => ({
    id: d.id, fileName: d.fileName, fileUrl: d.fileUrl, fileType: d.fileType,
    fileSize: d.fileSize, mimeType: d.mimeType, storagePath: d.storagePath,
  }));
}

async function resolveTemplateAutoAttachments(template: EmailTemplate) {
  const myGen = ++autoAttachGeneration;
  autoAttachMissingTypeIds.value = [];

  const types = template.autoAttachDocumentTypes;
  if (!types || types.length === 0 || !props.attachmentSource) {
    // Limpiar auto-adjuntos de una plantilla previa si esta no configura ninguno
    removePreviousAutoAttachments();
    if (myGen === autoAttachGeneration) {
      isResolvingAutoAttachments.value = false;
    }
    return;
  }

  isResolvingAutoAttachments.value = true;
  try {
    await ensureDocTypesLoaded();
    if (myGen !== autoAttachGeneration) return; // una invocación más reciente tomó el control

    // Quitar los auto-adjuntos de la plantilla anterior (no toca los manuales)
    removePreviousAutoAttachments();

    const entityFiles = await loadEntityFiles();
    if (myGen !== autoAttachGeneration) return;

    const alreadyAttached = (emailData.value.attachments || []).map(f => ({ name: f.name, size: f.size }));
    const { toAttach, missingTypeIds } = resolveAutoAttachments(entityFiles, types, alreadyAttached);

    const downloaded: File[] = [];
    const failed: string[] = [];
    for (const f of toAttach) {
      try {
        downloaded.push(await downloadAttachableFile(f));
      } catch (err) {
        console.error(err);
        failed.push(f.fileName);
      }
    }
    if (myGen !== autoAttachGeneration) return;

    addAttachments(downloaded, failed);

    // Rastrear por identidad SOLO los archivos que quedaron efectivamente adjuntos
    // (evita capturar adjuntos manuales agregados por el usuario durante la descarga)
    const current = emailData.value.attachments || [];
    templateAutoAttachments.value = downloaded.filter(f => current.includes(f));

    autoAttachMissingTypeIds.value = missingTypeIds;
  } catch (err) {
    // applyTemplate se invoca sin await/catch desde el template dropdown,
    // por lo que cualquier error de carga (tipos de doc, archivos de la entidad)
    // se registra aquí en vez de propagarse como unhandled rejection.
    console.error('[EmailComposer] Error resolviendo auto-adjuntos de plantilla:', err);
    showNotification(
      'error',
      t('emailComposer.notifications.attachFailed'),
      t('emailComposer.notifications.attachLoadErrorMsg')
    );
  } finally {
    if (myGen === autoAttachGeneration) {
      isResolvingAutoAttachments.value = false;
    }
  }
}

function removePreviousAutoAttachments() {
  if (templateAutoAttachments.value.length === 0) return;
  const prev = new Set(templateAutoAttachments.value);
  emailData.value.attachments = (emailData.value.attachments || []).filter(f => !prev.has(f));
  templateAutoAttachments.value = [];
}

function handleDragOver(event: DragEvent) {
  event.preventDefault();
  event.stopPropagation();
}

function handleDragEnter(event: DragEvent) {
  event.preventDefault();
  event.stopPropagation();
  // Solo archivos: una ficha de destinatario arrastrada sobre el cuerpo no es un adjunto
  if (!event.dataTransfer?.types?.includes('Files')) return;
  dragCounter++;
  isDragging.value = true;
}

function handleDragLeave(event: DragEvent) {
  event.preventDefault();
  event.stopPropagation();
  if (!event.dataTransfer?.types?.includes('Files')) return;
  dragCounter--;
  if (dragCounter === 0) {
    isDragging.value = false;
  }
}

async function handleDrop(event: DragEvent) {
  event.preventDefault();
  event.stopPropagation();
  isDragging.value = false;
  dragCounter = 0;

  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    await attachLocalFiles(Array.from(files));
  }
}

function removeAttachment(index: number) {
  emailData.value.attachments?.splice(index, 1);
}

/**
 * Vista previa de un adjunto local con el visor del buzón. El archivo no sale
 * del navegador: el visor lee una object URL. Se crea en cada apertura y se
 * revoca al cerrar; el estado guarda el adjunto ya adaptado y no el índice,
 * porque la lista de chips usa `:key="index"` y cambia al quitar uno.
 */
const previewAttachment = ref<EmailAttachment | null>(null);
let previewObjectUrl: string | null = null;

/** `File.type` puede venir vacío (.eml, .csv según el SO): se deduce del nombre. */
function canPreviewFile(file: File): boolean {
  return attachmentService.canPreview(resolveMimeType(file.type, file.name), file.name);
}

function openPreview(file: File) {
  closePreview();
  previewObjectUrl = URL.createObjectURL(file);
  previewAttachment.value = {
    id: file.name,
    name: file.name,
    contentType: resolveMimeType(file.type, file.name),
    size: file.size,
    downloadUrl: previewObjectUrl,
    isInline: false,
  };
}

function closePreview() {
  if (previewObjectUrl) URL.revokeObjectURL(previewObjectUrl);
  previewObjectUrl = null;
  previewAttachment.value = null;
}

/** Saca del reenvío un adjunto del original; no borra nada del correo. */
function removeInheritedAttachment(index: number) {
  const [removed] = inheritedAttachments.value.splice(index, 1);
  if (removed) excludedAttachments.value.push(removed);
}

/**
 * Agrega correos de Kila adjuntados por referencia, sin repetir por emailId.
 * Aplica el mismo tope de cantidad que el backend (10) y, cuando se conoce el
 * peso estimado, el mismo tope de 25 MB que ya cuentan los archivos propios:
 * antes solo se validaba por correo y tres de 10 MB pasaban en pantalla para
 * fallar recién al enviar, con el mensaje genérico.
 */
function addAttachedEmails(refs: AttachedEmailRef[]) {
  const existing = new Set(attachedEmails.value.map(r => r.emailId));
  const toAdd = refs.filter(r => !existing.has(r.emailId));
  if (toAdd.length === 0) return;

  const availableSlots = Math.max(0, MAX_ATTACHED_EMAILS - attachedEmails.value.length);
  const withinCount = toAdd.slice(0, availableSlots);
  const rejectedByCount = toAdd.length - withinCount.length;

  const alreadyAttachedSize = attachedEmails.value.reduce((s, r) => s + (r.estimatedSize || 0), 0);
  let runningTotal = currentAttachmentsSize.value + alreadyAttachedSize;
  let attemptedTotal = runningTotal;
  const accepted: AttachedEmailRef[] = [];
  const rejectedBySize: AttachedEmailRef[] = [];

  for (const ref of withinCount) {
    const size = ref.estimatedSize || 0;
    if (size > 0 && runningTotal + size > MAX_TOTAL_SIZE) {
      rejectedBySize.push(ref);
      attemptedTotal += size;
      continue;
    }
    runningTotal += size;
    attemptedTotal += size;
    accepted.push(ref);
  }

  if (accepted.length > 0) {
    attachedEmails.value.push(...accepted);
    revealAttachments();
    showNotification('success', t('emailComposer.asAttachment.added'), accepted.map(r => r.subject).join(', '));
  }

  if (rejectedBySize.length > 0) {
    showNotification(
      'warning',
      t('emailComposer.asAttachment.errorTitle'),
      t('emailComposer.asAttachment.tooBig', {
        size: (attemptedTotal / 1048576).toFixed(1),
        max: EMAIL_ATTACHMENT_TOTAL_LIMIT_MB,
      })
    );
  }

  if (rejectedByCount > 0) {
    showNotification(
      'warning',
      t('emailComposer.asAttachment.errorTitle'),
      t('emailComposer.asAttachment.tooMany', { max: MAX_ATTACHED_EMAILS })
    );
  }
}

function removeAttachedEmail(index: number) {
  attachedEmails.value.splice(index, 1);
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}


(['to', 'cc', 'bcc'] as const).forEach((field) => {
  watch(() => emailData.value[`${field}Input`], () => { recipientErrors.value[field] = []; });
});

async function sendEmail() {
  if (!settlePendingRecipients()) return;
  if (!canSend.value) return;
  const sendStartedAt = new Date();


  isSending.value = true;
  uiStore.setSessionSending(props.sessionId, true);

  try {
    // Las cid: del original vuelven de la URL de Storage con la que se ven en
    // el editor: el proveedor no entiende esas URL, solo cid: (ver loadOriginalEmail).
    // sanitizeEmailHtml cubre plantillas y borradores guardados antes del pegado limpio.
    const outgoingBody = urlsToCidImages(sanitizeEmailHtml(emailData.value.body), cidByUrl);
    const attachedEmailIds = attachedEmails.value.map(r => r.emailId);
    // Se leen del cuerpo sin sanitizar: el sanitizado quita los data-*
    const mentions = extractMentionEmails(emailData.value.body);

    let success = false;
    let skippedAttachments: string[] = [];
    // Mensaje concreto del backend (404/413/502 de un correo adjunto, por
    // ejemplo). Viaja en el resultado de esta llamada, no en `emailStore.error`
    // (singleton compartido por todos los compositores abiertos a la vez).
    let sendErrorMessage: string | undefined;

    // Si es una respuesta o responder a todos, usar el endpoint de reply de Microsoft Graph
    if ((props.mode === 'reply' || props.mode === 'replyAll') && props.emailId) {

      // Los destinatarios viajan siempre: el proveedor los resolvía del correo
      // original y lo que el usuario veía en Para/CC (plantilla o edición a
      // mano) se descartaba al enviar.
      const result = await emailStore.replyToEmail(props.emailId, {
        to: emailData.value.to,
        cc: emailData.value.cc,
        bcc: emailData.value.bcc,
        subject: emailData.value.subject,
        body: outgoingBody,
        bodyType: emailData.value.bodyType,
        attachments: emailData.value.attachments,
        attachedEmailIds,
        mentions,
        contextModule: props.contextModule || undefined,
        contextEntityId: props.contextEntityId || undefined,
        contextEntityIdNumber: props.contextEntityIdNumber || undefined,
      }, props.mode === 'replyAll');
      success = result.ok;
      sendErrorMessage = result.errorMessage;

    } else if (props.mode === 'forward' && props.emailId) {
      // El reenvío tiene que ir por el endpoint del proveedor: enviarlo como
      // correo nuevo perdía siempre los adjuntos del original.
      const result = await emailStore.forwardEmail(props.emailId, {
        to: emailData.value.to,
        cc: emailData.value.cc,
        bcc: emailData.value.bcc,
        subject: emailData.value.subject,
        body: outgoingBody,
        bodyType: emailData.value.bodyType,
        attachments: emailData.value.attachments,
        attachedEmailIds,
        mentions,
        excludeAttachments: excludedAttachments.value,
      });
      success = result.ok;
      skippedAttachments = result.skippedAttachments;
      sendErrorMessage = result.errorMessage;

    } else {
      // Para correos nuevos, usar sendEmail normal
      const result = await emailStore.sendEmail({
        ...emailData.value,
        body: outgoingBody,
        attachedEmailIds,
        mentions,
        contextModule: props.contextModule || undefined,
        contextEntityId: props.contextEntityId || undefined,
        contextEntityIdNumber: props.contextEntityIdNumber || undefined,
        // Respuesta a un correo embebido (.eml) sin hilo real: Gmail las usa,
        // Microsoft las ignora sin error (ver embeddedEmailReply.ts).
        inReplyTo: props.inReplyTo,
        references: props.references,
      });
      success = result.ok;
      sendErrorMessage = result.errorMessage;
    }
    
    if (success) {
      // Los adjuntos viajan con el email vía API. Cuando el email se sincroniza y asocia
      // al embarque/OC, copyEmailAttachmentsToShipment() en useEmailAssociation se encarga
      // de copiarlos a la subcolección files, evitando duplicados.

      // Aplicar trigger de plantilla si hay una activa y contexto de entidad

      if (activeTemplateId.value && activeTemplateName.value && props.templateContext) {
        // El auto-llenado de fecha define su propio módulo destino, así que no es
        // por entidad: va una sola vez, antes de los triggers.
        await applyAutoFillDateField(activeTemplateData.value, props.templateContext)

        // Se le aplica el trigger a TODAS las entidades del contexto, no a la
        // primera que exista. Antes era un if/else que probaba la OC primero: un
        // embarque con OC asociada trae las dos, así que una plantilla configurada
        // sobre un estado de embarque se buscaba en PURCHASE_ORDER_STATUS, no
        // aparecía y el embarque nunca cambiaba de estado. Cada módulo busca la
        // plantilla en su propia configuración; el que no la tenga no hace nada.
        const targets: Array<[TemplateTriggerModule, string]> = ([
          ['purchaseOrders', props.templateContext.purchaseOrders?.id],
          ['shipments', props.templateContext.shipments?.id],
          ['containers', props.templateContext.containers?.id]
        ] as Array<[TemplateTriggerModule, string | undefined]>)
          .filter((entry): entry is [TemplateTriggerModule, string] => !!entry[1])

        if (targets.length === 0) {
          console.warn('[TemplateTrigger] No se encontró ID de entidad en templateContext')
        }

        for (const [module, entityId] of targets) {
          await applyTemplateTrigger(
            activeTemplateId.value,
            activeTemplateName.value,
            module,
            entityId
          )
        }
      } else {
        console.warn('[TemplateTrigger] Condición no cumplida:', {
          hasTemplateId: !!activeTemplateId.value,
          hasTemplateName: !!activeTemplateName.value,
          hasContext: !!props.templateContext
        })
      }

      // Asociar automáticamente el email al embarque/OC si hay contexto de módulo (fire-and-forget)
      if (props.contextModule && props.contextEntityId) {
        if ((props.mode === 'reply' || props.mode === 'replyAll') && props.emailId) {
          associateReplyToContext(
            props.emailId,
            props.contextModule,
            props.contextEntityId,
            sendStartedAt,
            emailAuthStore.userEmail
          ).catch(err => console.warn('[EmailComposer] Error en asociación de reply:', err))
        } else if (props.mode === 'new') {
          associateNewEmailToContext(
            emailData.value.subject,
            props.contextModule,
            props.contextEntityId,
            sendStartedAt,
            emailAuthStore.userEmail
          ).catch(err => console.warn('[EmailComposer] Error en asociación de nuevo email:', err))
        }
      }

      // Dar por reenviado un correo al que le faltaron archivos es peor que no
      // reenviarlo: si el proveedor omitió alguno, se dice cuál.
      if (skippedAttachments.length > 0) {
        // showNotification y no uiStore: las notificaciones del uiStore no las
        // pinta ningún componente, y este aviso tiene que verse.
        showNotification(
          'warning',
          t('emailComposer.notifications.forwardedPartial'),
          t('emailComposer.notifications.forwardedPartialMsg', { list: skippedAttachments.join(', ') })
        );
      } else {
        uiStore.showSuccess(t('emailComposer.notifications.sent'), t('emailComposer.notifications.sentMsg'));
      }
      // Enviado: el borrador ya no hace falta
      discardDraft().catch(() => undefined);
      finished = true;
      uiStore.markComposerSent();
      uiStore.closeComposer(props.sessionId);

      // Recargar correos para ver la respuesta
      await emailStore.fetchEmails();
    } else {
      // showNotification y no uiStore: uiStore.showError no se ve. El mensaje
      // del backend (404/413/502 de un correo adjunto) viaja en el resultado
      // de esta llamada (ver sendErrorMessage), nunca en emailStore.error:
      // ese es un singleton que comparten todos los compositores abiertos.
      showNotification(
        'error',
        t('emailComposer.notifications.sendError'),
        sendErrorMessage || t('emailComposer.notifications.sendErrorMsg')
      );
    }
  } catch (error) {
    console.error('Error sending email:', error);
    showNotification(
      'error',
      t('emailComposer.notifications.sendError'),
      t('emailComposer.notifications.sendErrorUnexpected')
    );
  } finally {
    isSending.value = false;
    uiStore.setSessionSending(props.sessionId, false);
  }
}

// ── Borradores: se guardan en Kila (no en el proveedor) ──────────────────
// Autoguardado 10 s después del último cambio y al cerrar el compositor;
// al enviar o descartar se elimina. Antes "Guardar borrador" era un stub que
// solo mostraba un aviso ("los borradores no quedan guardados en Kila").
const draftId = ref<string | null>(props.draft?.id || null);
const lastSavedSnapshot = ref<string>('');
// true cuando el compositor se cierra "de verdad" (enviado, descartado, o
// cambio de compañía): onBeforeUnmount y el aviso nativo de recarga dejan de
// intentar guardar un borrador que ya no hace falta.
let finished = false;
let autosaveTimer: ReturnType<typeof setTimeout> | null = null;

function draftSnapshot(): string {
  const d = emailData.value;
  return JSON.stringify([
    d.to, d.cc, d.bcc, d.subject, d.body, d.importance,
    (d.attachments || []).map(f => f.name),
    attachedEmails.value.map(r => r.emailId),
  ]);
}

function hasDraftContent(): boolean {
  const d = emailData.value;
  return !!(d.subject.trim() || d.body.trim() || d.to.length || (d.attachments && d.attachments.length) || attachedEmails.value.length);
}

function buildDraftPayload(): EmailDraftInput {
  const d = emailData.value;
  return {
    mode: props.mode,
    inReplyTo: props.emailId || null,
    to: d.to,
    cc: d.cc,
    bcc: d.bcc,
    subject: d.subject,
    body: d.body,
    bodyType: d.bodyType || 'html',
    importance: d.importance,
    contextModule: props.contextModule || null,
    contextEntityId: props.contextEntityId || null,
    contextEntityIdNumber: props.contextEntityIdNumber || null,
    attachments: (d.attachments || []).map(f => ({ name: f.name, size: f.size, contentType: f.type })),
    attachedEmails: attachedEmails.value.map(({ emailId, subject }) => ({ emailId, subject })),
  };
}

async function persistDraft(): Promise<boolean> {
  if (!hasDraftContent()) return false;
  const snapshot = draftSnapshot();
  if (snapshot === lastSavedSnapshot.value) return true;

  const payload = buildDraftPayload();
  const saved = draftId.value
    ? await emailService.updateDraft(draftId.value, payload)
    : await emailService.saveDraft(payload);
  if (!saved) return false;

  draftId.value = saved.id;
  lastSavedSnapshot.value = snapshot;
  window.dispatchEvent(new CustomEvent('email:drafts-changed'));
  return true;
}

function scheduleAutosave() {
  if (autosaveTimer) clearTimeout(autosaveTimer);
  autosaveTimer = setTimeout(() => {
    persistDraft().catch(err => console.warn('[EmailComposer] autosave:', err));
  }, 10000);
}

watch(() => draftSnapshot(), () => {
  if (hasDraftContent()) scheduleAutosave();
});

async function discardDraft() {
  if (autosaveTimer) clearTimeout(autosaveTimer);
  if (!draftId.value) return;
  const id = draftId.value;
  draftId.value = null;
  await emailService.deleteDraft(id).catch(() => false);
  window.dispatchEvent(new CustomEvent('email:drafts-changed'));
}

async function saveDraft() {
  isSaving.value = true;
  try {
    const ok = await persistDraft();
    if (ok) {
      uiStore.showSuccess(t('emailComposer.notifications.draftSaved'), t('emailComposer.notifications.draftSavedMsg'));
    } else {
      uiStore.showError(t('emailComposer.notifications.draftSaved'), t('emailComposer.notifications.draftSaveError'));
    }
  } finally {
    isSaving.value = false;
  }
}

function loadDraft(draft: EmailDraft) {
  emailData.value.to = [...(draft.to || [])];
  emailData.value.cc = [...(draft.cc || [])];
  emailData.value.bcc = [...(draft.bcc || [])];
  emailData.value.subject = draft.subject || '';
  emailData.value.body = draft.body || '';
  emailData.value.bodyType = draft.bodyType || 'html';
  if (draft.importance) emailData.value.importance = draft.importance;
  // Une en vez de reemplazar: el watch de la cola de la sesión corre con
  // `immediate` desde el montaje, así que un "Reenviar como adjunto" que
  // llegue mientras el borrador todavía está cargando (onMounted espera
  // loadOriginalEmail antes de loadDraft) no debe perderse.
  const draftRefs = draft.attachedEmails || [];
  const draftIds = new Set(draftRefs.map(r => r.emailId));
  attachedEmails.value = [...draftRefs, ...attachedEmails.value.filter(r => !draftIds.has(r.emailId))];
  // Borradores guardados por versiones anteriores pueden traer la cita del
  // correo externo sin sanitizar
  renderEditor(emailData.value.body);
  lastSavedSnapshot.value = draftSnapshot();
}

const showDiscardModal = ref(false);

async function closeComposer() {
  // Cerrar guarda el borrador (como Gmail/Outlook); "Descartar" lo elimina.
  if (hasDraftContent()) {
    const ok = await persistDraft().catch(() => false);
    if (ok) {
      uiStore.showSuccess(t('emailComposer.notifications.draftSaved'), t('emailComposer.notifications.draftSavedMsg'));
      finished = true;
      uiStore.closeComposer(props.sessionId);
      return;
    }
    showDiscardModal.value = true;
    return;
  }
  await discardDraft();
  finished = true;
  uiStore.closeComposer(props.sessionId);
}

async function confirmDiscard() {
  showDiscardModal.value = false;
  await discardDraft();
  finished = true;
  uiStore.closeComposer(props.sessionId);
}

function cancelDiscard() {
  showDiscardModal.value = false;
}

function openTemplateEditor() {
  showTemplateDropdown.value = false;
  showTemplateEditor.value = true;
}

function onTemplateSaved(template: EmailTemplate) {
  showTemplateEditor.value = false;
  uiStore.showSuccess(t('emailComposer.notifications.templateCreated'), t('emailComposer.notifications.templateCreatedMsg', { name: template.name }));
}

/**
 * Carga los destinatarios configurados en la plantilla. REEMPLAZAN a los que
 * hubiera: al responder, los del hilo original no tienen por qué recibir la
 * plantilla. Antes se sumaban y el correo salía a gente que la plantilla no
 * incluía. Una plantilla sin destinatarios configurados no toca nada.
 */
function applyDefaultRecipients(template: EmailTemplate) {
  const defaults = template.defaultRecipients;
  if (!defaults) return;

  const clean = (list?: EmailAddress[]) => (list || []).filter(r => r.email);
  const to = clean(defaults.to);
  const cc = clean(defaults.cc);
  const bcc = clean(defaults.bcc);
  if (to.length === 0 && cc.length === 0 && bcc.length === 0) return;

  emailData.value.to = to;
  emailData.value.cc = cc;
  emailData.value.bcc = bcc;
  showCc.value = cc.length > 0;
  showBcc.value = bcc.length > 0;
}

async function applyTemplate(template: EmailTemplate) {
  appliedTemplateName.value = template.name;
  let subject = template.subject;
  let bodyHtml = template.bodyHtml;

  // Si hay contexto de datos (embarque/OC), resolver campos dinámicos
  if (props.templateContext) {

    const resolved = resolveTemplate(template, props.templateContext);
    subject = resolved.subject;
    bodyHtml = resolved.bodyHtml;

    if (resolved.unresolvedFields.length > 0) {
      console.warn('⚠️ Campos no resueltos:', resolved.unresolvedFields.map(f => `${f.module}.${f.fieldId}`));
    }
  } else {
    console.warn('⚠️ No templateContext provided - campos no serán resueltos');
  }

  // Si estamos en modo reply/replyAll/forward, preservar el contenido citado del hilo
  if (props.mode === 'reply' || props.mode === 'replyAll' || props.mode === 'forward') {
    // Buscar el contenido citado completo (todo después de los <br><br> iniciales)
    const currentBody = emailData.value.body;
    
    // La cita (nueva o de un borrador anterior a este cambio) se conserva
    // detrás del cuerpo de la plantilla.
    const { quote } = splitAtQuote(currentBody);
    if (quote) bodyHtml = bodyHtml + quote;

    // Un solo prefijo de hilo en el asunto, sin acumular.
    subject = props.mode === 'forward' ? buildForwardSubject(subject, locale.value) : buildReplySubject(subject);

    // Antes la plantilla pisaba siempre el asunto del hilo y se perdían los
    // consecutivos del cliente. Se guardan los dos y manda la última elección
    // del usuario (selector bajo el asunto).
    templateSubject.value = subject;
    if (subjectSource.value === 'thread' && threadSubject.value) {
      subject = threadSubject.value;
    }
  }

  emailData.value.subject = subject;
  emailData.value.body = bodyHtml;
  // bodyHtml puede arrastrar la cita del correo externo (reply/forward)
  renderEditor(bodyHtml);
  applyDefaultRecipients(template);
  showTemplateDropdown.value = false;
  templateStore.incrementUsageCount(template.id);
  activeTemplateId.value = template.id;
  activeTemplateName.value = template.name;
  activeTemplateData.value = template;

  // Re-insertar firma predeterminada después de aplicar plantilla
  const defaultSig = signatureStore.defaultSignature;
  if (defaultSig) {
    insertSignature(defaultSig);
  }

  await resolveTemplateAutoAttachments(template);
}

const hasActiveSignature = computed(() => {
  if (!editorRef.value) return false;
  return !!editorRef.value.querySelector(`.${SIGNATURE_WRAPPER_CLASS}`);
});

function insertSignature(signature: EmailSignature) {
  removeExistingSignature();

  // Solo un separador: la firma se muestra tal cual la configuró el usuario.
  // Antes el contenedor imponía color gris, tamaño 13px y una línea superior
  // que cambiaban el diseño de la firma ("la firma no se toma bien").
  const signatureHtml = `<div class="${SIGNATURE_WRAPPER_CLASS}" style="margin-top: 16px;">${signature.bodyHtml}</div>`;

  if (!editorRef.value) return;

  const { before, quote } = splitAtQuote(emailData.value.body);
  emailData.value.body = quote ? before + signatureHtml + quote : emailData.value.body + signatureHtml;

  renderEditor(emailData.value.body);

  showSignatureDropdown.value = false;
}

function removeExistingSignature() {
  if (editorRef.value) {
    const existing = editorRef.value.querySelector(`.${SIGNATURE_WRAPPER_CLASS}`);
    if (existing) {
      existing.remove();
      emailData.value.body = editorRef.value.innerHTML;
    }
  }
}

function handleGlobalClick(e: MouseEvent) {
  const target = e.target as HTMLElement;
  // Los menús viven fuera del redactor (Teleport): un clic dentro de ellos no los cierra.
  const isInside = (...els: Array<HTMLElement | null>) => els.some((el) => el?.contains(target));
  if (attachmentDropdownRef.value && !isInside(attachmentDropdownRef.value, attachmentMenuRef.value)) {
    showAttachmentDropdown.value = false;
  }
  if (templateDropdownRef.value && !isInside(templateDropdownRef.value, templateMenuRef.value)) {
    showTemplateDropdown.value = false;
  }
  if (signatureDropdownRef.value && !isInside(signatureDropdownRef.value, signatureMenuRef.value)) {
    showSignatureDropdown.value = false;
  }
  if (priorityDropdownRef.value && !isInside(priorityDropdownRef.value, priorityMenuRef.value)) {
    showPriorityDropdown.value = false;
  }
}

onMounted(async () => {
  document.addEventListener('click', handleGlobalClick);
  window.addEventListener('beforeunload', handleBeforeUnload);

  if (props.mode !== 'new' && props.emailId) {
    await loadOriginalEmail();
  }

  // Retomar un borrador guardado en Kila: su contenido manda sobre la
  // plantilla y la firma por defecto (ya venían dentro del cuerpo guardado)
  if (props.draft) {
    loadDraft(props.draft);
    if (templateStore.templates.length === 0) templateStore.fetchTemplates();
    if (signatureStore.signatures.length === 0) signatureStore.fetchSignatures();
    return;
  }

  // Asunto con el que arranca un correo nuevo (p.ej. "Reenviar como adjunto"
  // sin compositores abiertos: RV: <asunto del correo adjuntado>).
  if (props.mode === 'new' && props.initialSubject && !emailData.value.subject) {
    emailData.value.subject = props.initialSubject;
  }

  // Respuesta o reenvío de un correo que llegó como adjunto: arranca con los
  // destinatarios y la cita ya armados (utils/embeddedEmailReply.ts).
  if (props.mode === 'new') {
    if (props.initialTo?.length) emailData.value.to = [...props.initialTo];
    if (props.initialCc?.length) { emailData.value.cc = [...props.initialCc]; showCc.value = true; }
    if (props.initialBodyHtml) { emailData.value.body = props.initialBodyHtml; renderEditor(emailData.value.body); }
  }

  // Cargar plantilla inicial si se proporcionó
  if (props.initialTemplate) {
    applyTemplate(props.initialTemplate);
  }

  // Asegurar que las plantillas estén cargadas
  if (templateStore.templates.length === 0) {
    templateStore.fetchTemplates();
  }

  // Cargar firmas y auto-insertar la predeterminada
  if (signatureStore.signatures.length === 0) {
    await signatureStore.fetchSignatures();
  }
  const defaultSig = signatureStore.defaultSignature;
  if (defaultSig) {
    insertSignature(defaultSig);
  }

  // En correo nuevo el foco se queda en "Para"; al responder o reenviar los
  // destinatarios ya están puestos y lo que sigue es escribir el mensaje.
  // Igual si el correo nuevo ya trae destinatarios (respuesta a un embebido).
  if (props.mode !== 'new' || props.initialTo?.length) focusEditorStart();

  if (props.openTemplatesOnMount) showTemplateDropdown.value = true;
});

/**
 * Aviso nativo del navegador si hay cambios sin autoguardar. Solo importa al
 * salir del dashboard (logout, cerrar la pestaña): moverse entre en línea y
 * flotante no desmonta el componente (Teleport), así que esto nunca dispara
 * por eso.
 */
function handleBeforeUnload(e: BeforeUnloadEvent) {
  if (!finished && hasDraftContent() && draftSnapshot() !== lastSavedSnapshot.value) {
    e.preventDefault();
    e.returnValue = '';
  }
}

// Cambiar de compañía invalida el token del compositor: se cierra sin
// preguntar y lo último guardado queda en Borradores de la compañía anterior.
watch(() => companyStore.selectedCompanyId, (next, prev) => {
  if (prev && next !== prev) {
    finished = true;
    if (autosaveTimer) clearTimeout(autosaveTimer);
    showNotification(
      'warning',
      t('emailComposer.notifications.closedByCompanyChange'),
      t('emailComposer.notifications.closedByCompanyChangeMsg')
    );
    uiStore.closeComposer(props.sessionId);
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleGlobalClick);
  window.removeEventListener('beforeunload', handleBeforeUnload);
  if (autosaveTimer) clearTimeout(autosaveTimer);
  if (highlightTimer) clearTimeout(highlightTimer);
  closePreview();

  // Solo pasa al salir del dashboard (logout): moverse entre en línea y
  // flotante no desmonta el componente.
  if (!finished && !isSending.value && hasDraftContent()) {
    persistDraft()
      .then(ok => {
        if (ok) {
          showNotification(
            'success',
            t('emailComposer.notifications.previousDraftSaved'),
            t('emailComposer.notifications.previousDraftSavedMsg')
          );
        }
      })
      .catch(err => console.warn('[EmailComposer] autosave al desmontar:', err));
  }
});

// Consumir la cola de archivos/correos adjuntos de la sesión: los agrega
// useEmailAsAttachment (u otros abridores) cuando el compositor ya existía.
// Al final del script, porque currentAttachmentsSize (más arriba) daría un
// error de TDZ si esto se declarara antes.
const sessionState = computed(() => uiStore.getComposerSession(props.sessionId));
watch(() => [sessionState.value?.pendingFiles.length, sessionState.value?.pendingAttachedEmails.length], () => {
  const s = sessionState.value;
  if (!s || (s.pendingFiles.length === 0 && s.pendingAttachedEmails.length === 0)) return;
  const { files, attachedEmails: refs } = uiStore.takeSessionQueues(props.sessionId);
  if (files.length) addAttachments(files);
  if (refs.length) addAttachedEmails(refs);
}, { immediate: true });
</script>
